import { useState, useCallback, useRef } from 'react';

interface BluetoothPrinterState {
  device: BluetoothDevice | null;
  server: BluetoothRemoteGATTServer | null;
  characteristic: BluetoothRemoteGATTCharacteristic | null;
  connected: boolean;
  connecting: boolean;
  error: string | null;
}

interface PrintContent {
  title?: string;
  lines: { text: string; align?: 'left' | 'center' | 'right'; bold?: boolean; size?: 'normal' | 'large' | 'small' }[];
  cut?: boolean;
}

// ESC/POS command bytes
const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;
const INIT = [ESC, 0x40]; // Initialize printer
const ALIGN_LEFT = [ESC, 0x61, 0x00];
const ALIGN_CENTER = [ESC, 0x61, 0x01];
const ALIGN_RIGHT = [ESC, 0x61, 0x02];
const BOLD_ON = [ESC, 0x45, 0x01];
const BOLD_OFF = [ESC, 0x45, 0x00];
const FONT_NORMAL = [ESC, 0x21, 0x00];
const FONT_LARGE = [ESC, 0x21, 0x30]; // Double width + height
const FONT_SMALL = [ESC, 0x21, 0x01];
const CUT_PAPER = [GS, 0x56, 0x01]; // Partial cut
const LINE_FEED = [LF];

function alignCmd(a: string): number[] {
  if (a === 'center') return ALIGN_CENTER;
  if (a === 'right') return ALIGN_RIGHT;
  return ALIGN_LEFT;
}

function sizeCmd(s: string): number[] {
  if (s === 'large') return FONT_LARGE;
  if (s === 'small') return FONT_SMALL;
  return FONT_NORMAL;
}

function textToBytes(text: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(text);
}

function buildEscPosCommands(content: PrintContent): Uint8Array {
  const chunks: Uint8Array[] = [];

  // Initialize
  chunks.push(new Uint8Array(INIT));

  // Title
  if (content.title) {
    chunks.push(new Uint8Array(ALIGN_CENTER));
    chunks.push(new Uint8Array(FONT_LARGE));
    chunks.push(new Uint8Array(BOLD_ON));
    chunks.push(textToBytes(content.title));
    chunks.push(new Uint8Array(BOLD_OFF));
    chunks.push(new Uint8Array(FONT_NORMAL));
    chunks.push(new Uint8Array(LINE_FEED));
    chunks.push(new Uint8Array(LINE_FEED));
  }

  for (const line of content.lines) {
    chunks.push(new Uint8Array(alignCmd(line.align || 'left')));
    chunks.push(new Uint8Array(sizeCmd(line.size || 'normal')));
    if (line.bold) chunks.push(new Uint8Array(BOLD_ON));
    chunks.push(textToBytes(line.text));
    if (line.bold) chunks.push(new Uint8Array(BOLD_OFF));
    chunks.push(new Uint8Array(LINE_FEED));
  }

  // Footer spacing
  chunks.push(new Uint8Array(LINE_FEED));
  chunks.push(new Uint8Array(LINE_FEED));

  if (content.cut !== false) {
    chunks.push(new Uint8Array(CUT_PAPER));
  }

  const totalLen = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(totalLen);
  let offset = 0;
  for (const c of chunks) {
    result.set(c, offset);
    offset += c.length;
  }
  return result;
}

export function useBluetoothPrinter() {
  const [state, setState] = useState<BluetoothPrinterState>({
    device: null,
    server: null,
    characteristic: null,
    connected: false,
    connecting: false,
    error: null,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const isSupported = typeof navigator !== 'undefined' && 'bluetooth' in navigator;

  const connect = useCallback(async () => {
    if (!isSupported) {
      setState(s => ({ ...s, error: 'Trình duyệt không hỗ trợ Bluetooth. Vui lòng dùng Chrome trên Android hoặc Chrome/Edge trên máy tính.' }));
      return;
    }

    setState(s => ({ ...s, connecting: true, error: null }));

    try {
      const device = await (navigator as unknown as { bluetooth: { requestDevice: (opt: unknown) => Promise<BluetoothDevice> } })
        .bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb', '0000ff00-0000-1000-8000-00805f9b34fb', '49535343-fe7d-4ae5-8fa9-9fafd205e455'],
        });

      const server = await device.gatt?.connect();
      if (!server) {
        throw new Error('Không thể kết nối GATT server');
      }

      // Try common printer services
      const servicesToTry = [
        '000018f0-0000-1000-8000-00805f9b34fb',
        '0000ff00-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
      ];

      let service: BluetoothRemoteGATTService | null = null;
      for (const svcUuid of servicesToTry) {
        try {
          service = await server.getPrimaryService(svcUuid);
          if (service) break;
        } catch {
          // continue trying
        }
      }

      if (!service) {
        // Fallback: try listing services
        const services = await server.getPrimaryServices();
        if (services.length > 0) {
          service = services[0];
        } else {
          throw new Error('Không tìm thấy service máy in. Vui lòng kiểm tra máy in đã bật chưa.');
        }
      }

      // Try to find write characteristic
      const characteristics = await service.getCharacteristics();
      const writeChar = characteristics.find(c =>
        c.properties.write || c.properties.writeWithoutResponse
      );

      if (!writeChar) {
        throw new Error('Không tìm thấy characteristic ghi dữ liệu.');
      }

      setState({
        device,
        server,
        characteristic: writeChar,
        connected: true,
        connecting: false,
        error: null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Lỗi kết nối Bluetooth';
      setState(s => ({ ...s, connecting: false, error: msg, connected: false }));
    }
  }, [isSupported]);

  const disconnect = useCallback(async () => {
    const { server, device } = stateRef.current;
    try {
      if (server && server.connected) {
        server.disconnect();
      }
    } catch {
      // ignore
    }
    setState({
      device: null,
      server: null,
      characteristic: null,
      connected: false,
      connecting: false,
      error: null,
    });
  }, []);

  const print = useCallback(async (content: PrintContent) => {
    const { characteristic, connected } = stateRef.current;
    if (!connected || !characteristic) {
      throw new Error('Chưa kết nối máy in Bluetooth');
    }

    const data = buildEscPosCommands(content);
    const chunkSize = 512;

    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      await characteristic.writeValue(chunk);
    }
  }, []);

  const printPawnTicket = useCallback(async (params: {
    shopName: string;
    shopAddress: string;
    shopPhone: string;
    contractNo: string;
    customerName: string;
    customerPhone: string;
    cccd: string;
    cccdDate: string;
    hktt: string;
    assetName: string;
    brand: string;
    engineNo: string;
    frameNo: string;
    loanAmount: number;
    loanAmountWords: string;
    startDate: string;
    dueDate: string;
    interestRate: number;
    interestPerMonth: number;
    totalInterest: number;
    totalPayback: number;
    days: number;
  }) => {
    const p = params;
    const content: PrintContent = {
      title: 'HOP DONG CAM DO',
      lines: [
        { text: p.shopName, align: 'center', bold: true },
        { text: p.shopAddress, align: 'center', size: 'small' },
        { text: `SĐT: ${p.shopPhone}`, align: 'center', size: 'small' },
        { text: '', align: 'center' },
        { text: `Liên: ${p.contractNo || '...'}`, align: 'center', size: 'small' },
        { text: '--------------------------------', align: 'center' },
        { text: `Ben A (Nhan cam co): ${p.shopName}`, align: 'left', size: 'small' },
        { text: `Ben B (Cam co): ${p.customerName}`, align: 'left', bold: true },
        { text: `SĐT: ${p.customerPhone || '...'}`, align: 'left', size: 'small' },
        { text: `CCCD: ${p.cccd || '...'} | Cap: ${p.cccdDate || '...'}`, align: 'left', size: 'small' },
        { text: `HKTT: ${p.hktt || '...'}`, align: 'left', size: 'small' },
        { text: '', align: 'left' },
        { text: `Tai san: ${p.assetName || '...'}`, align: 'left', bold: true },
        { text: `Nhan hieu: ${p.brand || '...'}`, align: 'left', size: 'small' },
        { text: `So may: ${p.engineNo || '...'} | So khung: ${p.frameNo || '...'}`, align: 'left', size: 'small' },
        { text: '', align: 'left' },
        { text: `So tien cam: ${p.loanAmount.toLocaleString('vi-VN')}đ`, align: 'left', bold: true },
        { text: `(Bang chu: ${p.loanAmountWords})`, align: 'left', size: 'small' },
        { text: '', align: 'left' },
        { text: `Tu ngay: ${p.startDate} den ${p.dueDate}`, align: 'left', size: 'small' },
        { text: `Lai suat: ${p.interestRate}%/thang`, align: 'left', size: 'small' },
        { text: `Lai/thang: ${p.interestPerMonth.toLocaleString('vi-VN')}đ`, align: 'left', size: 'small' },
        { text: `Tong lai (${p.days} ngay): ${p.totalInterest.toLocaleString('vi-VN')}đ`, align: 'left', size: 'small' },
        { text: `Tong phai tra: ${p.totalPayback.toLocaleString('vi-VN')}đ`, align: 'left', bold: true },
        { text: '', align: 'left' },
        { text: '--------------------------------', align: 'center' },
        { text: 'CAM KET:', align: 'center', bold: true },
        { text: 'Ben B chiu trach nhiem phap ly ve tai san cam co; tra no goc, lai day du dung han. Qua han, Ben A toan quyen xu ly tai san.', align: 'left', size: 'small' },
        { text: 'Ben A bao quan tai san dung hien trang ban dau; hoan tra day du sau khi thanh toan xong.', align: 'left', size: 'small' },
        { text: '', align: 'center' },
        { text: 'Ben B (Cam co)', align: 'left', size: 'small' },
        { text: '(Ky & ghi ro ho ten)', align: 'left', size: 'small' },
        { text: '', align: 'left' },
        { text: '', align: 'left' },
        { text: 'Ben A (Nhan cam co)', align: 'right', size: 'small' },
        { text: '(Ky & ghi ro ho ten)', align: 'right', size: 'small' },
        { text: '', align: 'center' },
        { text: `In ngay: ${new Date().toLocaleString('vi-VN')}`, align: 'center', size: 'small' },
      ],
      cut: true,
    };

    await print(content);
  }, [print]);

  return {
    ...state,
    isSupported,
    connect,
    disconnect,
    print,
    printPawnTicket,
  };
}